import * as THREE from 'three';
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader.js';


export const name = 'ttPlyLoader';

export const BASE_ASSET_URL = import.meta.env.DEV
    ? '/remote-assets'
    : 'https://tunneltwin.nbg1.your-objectstorage.com';

export interface plyLoadOptions {
    projectName: string;
    url: string;
    contents?: string;
    transformation?: number[];
    convertPlyYUpToZUp?: boolean;
    signal?: AbortSignal;
}

function errorMessage(error: unknown): string {
    if (error instanceof Error) return `${error.name}: ${error.message}`;
    return String(error);
}

function contextualError(message: string, cause: unknown): Error {
    const error = new Error(`${message}: ${errorMessage(cause)}`);
    (error as Error & { cause?: unknown }).cause = cause;
    return error;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasNonIdentityTransform(transformation?: number[]): boolean {
    if (!transformation || transformation.length !== 16) return false;
    return !new THREE.Matrix4().fromArray(transformation).equals(new THREE.Matrix4());
}

function applyProjectTransformation(buffer: THREE.BufferGeometry<THREE.NormalBufferAttributes>, transformation?: number[]) {
    if (!transformation || transformation.length !== 16) return;
    const matrix = new THREE.Matrix4().fromArray(transformation);
    if (!matrix.equals(new THREE.Matrix4())) {
        buffer.applyMatrix4(matrix);
    }
}

/** Load exactly one Ply URL. Project metadata is deliberately supplied by the caller. */
export async function loadPlyAsset(options: plyLoadOptions): Promise<THREE.Points> {
    const loader = new PLYLoader();
    loader.setCrossOrigin('anonymous');
    try {
        var url = options.url;
        if(!options.url.startsWith('blob:http')){
            url = `${BASE_ASSET_URL}/models/${encodeURIComponent(options.projectName)}/${options.url}`;
        }
        const response = await fetch(url, {
            credentials: 'same-origin',
            signal: options.signal,
        });
        if (!response.ok) {
            throw new Error(`GLB request failed with HTTP ${response.status} ${response.statusText}`);
        }
        const data = await response.arrayBuffer();
        if (options.signal?.aborted) {
            throw new DOMException('PLY load cancelled.', 'AbortError');
        }
        
        const plyGeometryBuffer = await loader.parse(data);
        plyGeometryBuffer.computeVertexNormals();
        const material = new THREE.PointsMaterial({ size: 0.02, vertexColors: true });
        
        if (options.convertPlyYUpToZUp) {
            plyGeometryBuffer.applyMatrix4(new THREE.Matrix4().makeRotationX(Math.PI / 2));
        }
        if(options.transformation)
            applyProjectTransformation(plyGeometryBuffer, options.transformation);

        return new THREE.Points(plyGeometryBuffer, material);;
    } catch (error) {
        if ((error as DOMException)?.name === 'AbortError') throw error;
        const detailedError = contextualError(`Failed to load PLY ${options.url}`, error);
        console.error(detailedError);
        throw detailedError;
    } 
}

/** Legacy compatibility wrapper retained for external call sites. */
export async function ttPlyLoader(projectName: string, modelName: string): Promise<THREE.Object3D[]> {
    const url = `${BASE_ASSET_URL}/models/${encodeURIComponent(projectName)}/pointclouds/${encodeURIComponent(modelName)}`;
    return [await loadPlyAsset({ projectName, url})];
}
